import axios from "axios";
import { getApiBaseUrl } from "../../config/api";
import {
    getRequest,
    getSchemesSuccess,
    getSchemeSuccess,
    getLookupSuccess,
    getCataloguePreviewSuccess,
    stuffDone,
    getFailed,
    getError,
} from "./schemeSlice";

function isFailureMessage(data) {
    return Boolean(data?.message && !data?.id && !data?._id);
}

function dispatchMutationResult(dispatch, data) {
    if (isFailureMessage(data)) {
        dispatch(getFailed(data.message));
    } else {
        dispatch(stuffDone());
    }
}

function errorMessage(error, fallback = "Request failed") {
    const data = error?.response?.data;
    if (typeof data === "object" && data?.message) return data.message;
    if (typeof data === "string" && data.trim()) return data;
    return error?.message || fallback;
}

export const createScheme = (data) => async (dispatch) => {
    dispatch(getRequest());
    try {
        const result = await axios.post(`${getApiBaseUrl()}/SchemeOfWork`, data);
        dispatchMutationResult(dispatch, result.data);
    } catch (error) {
        dispatch(getError(errorMessage(error, "Could not create scheme")));
    }
};

export const getMySchemes = (teacherId, filters = {}) => async (dispatch) => {
    dispatch(getRequest());
    try {
        const query = new URLSearchParams(filters).toString();
        const url = query
            ? `${getApiBaseUrl()}/MySchemes/${teacherId}?${query}`
            : `${getApiBaseUrl()}/MySchemes/${teacherId}`;
        const result = await axios.get(url);
        if (result.data?.message && !Array.isArray(result.data)) {
            dispatch(getFailed(result.data.message));
        } else {
            dispatch(getSchemesSuccess(Array.isArray(result.data) ? result.data : []));
        }
    } catch (error) {
        dispatch(getError(errorMessage(error, "Could not load schemes")));
    }
};

export const getSchemeById = (id) => async (dispatch) => {
    dispatch(getRequest());
    try {
        const result = await axios.get(`${getApiBaseUrl()}/SchemeOfWork/${id}`);
        if (isFailureMessage(result.data)) {
            dispatch(getFailed(result.data.message));
        } else {
            dispatch(getSchemeSuccess(result.data));
        }
    } catch (error) {
        dispatch(getError(errorMessage(error, "Could not load scheme")));
    }
};

export const updateScheme = (id, data) => async (dispatch) => {
    dispatch(getRequest());
    try {
        const result = await axios.put(`${getApiBaseUrl()}/SchemeOfWork/${id}`, data);
        dispatchMutationResult(dispatch, result.data);
    } catch (error) {
        dispatch(getError(errorMessage(error, "Could not update scheme")));
    }
};

export const deleteScheme = (id) => async (dispatch) => {
    dispatch(getRequest());
    try {
        const result = await axios.delete(`${getApiBaseUrl()}/SchemeOfWork/${id}`);
        dispatchMutationResult(dispatch, result.data);
    } catch (error) {
        dispatch(getError(errorMessage(error, "Could not delete scheme")));
    }
};

export const addEntry = (schemeId, entryData) => async (dispatch) => {
    dispatch(getRequest());
    try {
        const result = await axios.post(`${getApiBaseUrl()}/SchemeOfWork/${schemeId}/entry`, entryData);
        dispatchMutationResult(dispatch, result.data);
    } catch (error) {
        dispatch(getError(errorMessage(error, "Could not add scheme entry")));
    }
};

export const updateEntry = (entryId, data) => async (dispatch) => {
    dispatch(getRequest());
    try {
        const result = await axios.put(`${getApiBaseUrl()}/SchemeEntry/${entryId}`, data);
        dispatchMutationResult(dispatch, result.data);
    } catch (error) {
        dispatch(getError(errorMessage(error, "Could not update entry")));
    }
};

export const markEntryTaught = (entryId, data) => async (dispatch) => {
    dispatch(getRequest());
    try {
        const result = await axios.put(`${getApiBaseUrl()}/SchemeEntry/${entryId}/taught`, data);
        dispatchMutationResult(dispatch, result.data);
    } catch (error) {
        dispatch(getError(errorMessage(error, "Could not update entry")));
    }
};

export const deleteEntry = (entryId) => async (dispatch) => {
    dispatch(getRequest());
    try {
        const result = await axios.delete(`${getApiBaseUrl()}/SchemeEntry/${entryId}`);
        dispatchMutationResult(dispatch, result.data);
    } catch (error) {
        dispatch(getError(errorMessage(error, "Could not delete entry")));
    }
};

export const getSchemeLookups = (subject, form, term) => async (dispatch) => {
    dispatch(getRequest());
    try {
        const params = new URLSearchParams();
        if (subject) params.set("subject", subject);
        if (form) params.set("form", form);
        if (term) params.set("term", term);
        const query = params.toString() ? `?${params.toString()}` : "";
        const result = await axios.get(`${getApiBaseUrl()}/SchemeLookups${query}`);
        if (result.data?.message && !result.data?.coreElements) {
            dispatch(getFailed(result.data.message));
        } else {
            dispatch(getLookupSuccess(result.data));
        }
    } catch (error) {
        dispatch(getError(errorMessage(error, "Could not load scheme lookups")));
    }
};

export const checkCataloguePreview = (subject, form, term) => async (dispatch) => {
    dispatch(getRequest());
    try {
        const params = new URLSearchParams({ subject, form, term }).toString();
        const result = await axios.get(
            `${getApiBaseUrl()}/SchemeOfWork/catalogue/preview?${params}`
        );
        dispatch(getCataloguePreviewSuccess(result.data));
    } catch (error) {
        if (error?.response?.status === 404) {
            dispatch(getCataloguePreviewSuccess({
                hasTemplate: false,
                entryCount: 0,
                topicPreview: [],
            }));
        } else {
            dispatch(getError(errorMessage(error, "Could not check template availability")));
        }
    }
};

export const bulkLoadFromCatalogue = (schemeId, data) => async (dispatch) => {
    dispatch(getRequest());
    try {
        const result = await axios.post(
            `${getApiBaseUrl()}/SchemeOfWork/${schemeId}/load-catalogue`,
            data
        );
        dispatchMutationResult(dispatch, result.data);
    } catch (error) {
        const msg = error?.response?.data?.message;
        if (msg) {
            dispatch(getFailed(msg));
        } else {
            dispatch(getError(errorMessage(error, "Could not load template entries")));
        }
    }
};
